import { useEffect, useState } from 'react'
import { Table, Button, Input, Modal, Form, message, Tag, Popconfirm, Space, Tooltip } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined, SearchOutlined, FileExcelOutlined, DownloadOutlined } from '@ant-design/icons'
import api, { contactsApi } from '../services/api'

const { TextArea } = Input

interface Contact {
    id: number
    tg_user_id: number
    username: string | null
    first_name: string | null
    last_name: string | null
    phone: string | null
    notes: string | null
    tags: string[]
    created_at: string
}

function Contacts() {
    const [contacts, setContacts] = useState<Contact[]>([])
    const [loading, setLoading] = useState(false)
    const [modalOpen, setModalOpen] = useState(false)
    const [editingContact, setEditingContact] = useState<Contact | null>(null)
    const [form] = Form.useForm()
    const [searchQuery, setSearchQuery] = useState('')
    const [loadingExport, setLoadingExport] = useState(false)

    // Export contacts to Excel
    const exportContacts = async () => {
        setLoadingExport(true)
        try {
            const response = await api.get('/contacts/export/excel?all=true', { responseType: 'blob' })
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `contacts_${new Date().toISOString().split('T')[0]}.xlsx`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
            message.success('Контакты экспортированы!')
        } catch {
            message.error('Ошибка экспорта')
        } finally {
            setLoadingExport(false)
        }
    }

    useEffect(() => {
        fetchContacts()
    }, [])

    const fetchContacts = async () => {
        setLoading(true)
        try {
            const response = await contactsApi.getAll()
            setContacts(response.data)
        } catch {
            message.error('Ошибка загрузки контактов')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        const token = localStorage.getItem('token')
        if (!token) return
        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
        const wsUrl = `${wsProtocol}//${window.location.host}/ws?token=${token}`
        
        let ws: WebSocket | null = null
        let reconnectTimeout: ReturnType<typeof setTimeout> | null = null
        let reconnectDelay = 1000
        let isClosed = false

        const connect = () => {
            if (isClosed) return
            ws = new WebSocket(wsUrl)
            ws.onopen = () => { reconnectDelay = 1000 }
            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data)
                    if (data.type === 'contact_update') {
                        setContacts(prev => prev.map(c => 
                            c.tg_user_id === data.contact.chat_id
                                ? { 
                                    ...c, 
                                    first_name: data.contact.first_name,
                                    last_name: data.contact.last_name,
                                    username: data.contact.username
                                }
                                : c
                        ))
                    }
                } catch (e) {
                    console.error('WebSocket parse error:', e)
                }
            }
            ws.onclose = () => {
                if (isClosed) return
                reconnectTimeout = setTimeout(connect, reconnectDelay)
                reconnectDelay = Math.min(reconnectDelay * 2, 10000)
            }
            ws.onerror = (e) => {
                console.error('WebSocket error:', e)
                ws?.close()
            }
        }

        connect()

        return () => {
            isClosed = true
            if (reconnectTimeout) clearTimeout(reconnectTimeout)
            ws?.close()
        }
    }, [])

    const handleCreate = () => {
        setEditingContact(null)
        form.resetFields()
        setModalOpen(true)
    }

    const handleEdit = (contact: Contact) => {
        setEditingContact(contact)
        form.setFieldsValue({
            username: contact.username,
            phone: contact.phone,
            first_name: contact.first_name,
            last_name: contact.last_name,
            notes: contact.notes,
            tags: contact.tags?.join(', ') || ''
        })
        setModalOpen(true)
    }

    const handleDelete = async (id: number) => {
        try {
            await contactsApi.delete(id)
            message.success('Контакт удалён')
            fetchContacts()
        } catch {
            message.error('Ошибка удаления')
        }
    }

    const handleSubmit = async (values: any) => {
        try {
            const data = {
                ...values,
                tags: values.tags ? values.tags.split(',').map((t: string) => t.trim()).filter(Boolean) : []
            }

            if (editingContact) {
                await contactsApi.update(editingContact.id, data)
                message.success('Контакт обновлён')
            } else {
                await contactsApi.create(data)
                message.success('Контакт создан')
            }
            setModalOpen(false)
            fetchContacts()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка сохранения')
        }
    }

    const columns = [
        {
            title: 'Имя',
            key: 'name',
            render: (record: Contact) => (
                <span>
                    {record.first_name || 'Без имени'} {record.last_name || ''}
                </span>
            )
        },
        {
            title: 'Username',
            dataIndex: 'username',
            key: 'username',
            render: (val: string) => val ? `@${val}` : '-'
        },
        {
            title: 'Телефон',
            dataIndex: 'phone',
            key: 'phone',
            render: (val: string) => val || '-'
        },
        {
            title: 'Заметка',
            dataIndex: 'notes',
            key: 'notes',
            render: (val: string) => val ? (
                <span style={{ color: '#888', fontSize: 12 }}>{val.substring(0, 50)}{val.length > 50 ? '...' : ''}</span>
            ) : '-'
        },
        {
            title: 'Теги',
            dataIndex: 'tags',
            key: 'tags',
            render: (tags: string[]) => tags?.length > 0 ? (
                <Space size={4}>
                    {tags.slice(0, 3).map(tag => <Tag key={tag} color="blue">{tag}</Tag>)}
                    {tags.length > 3 && <Tag>+{tags.length - 3}</Tag>}
                </Space>
            ) : '-'
        },
        {
            title: 'Действия',
            key: 'actions',
            width: 120,
            render: (record: Contact) => (
                <Space>
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    />
                    <Popconfirm
                        title="Удалить контакт?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Да"
                        cancelText="Нет"
                    >
                        <Button type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            )
        }
    ]

    return (
        <div style={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <h2 style={{ color: 'var(--text-primary)', margin: 0 }}>Контакты</h2>
                <Space>
                    {/* <Tooltip title="Выгрузить в Excel">
                        <Button
                            icon={<FileExcelOutlined />}
                            onClick={exportContacts}
                            loading={loadingExport}
                            style={{
                                borderColor: '#10b981',
                                color: '#10b981'
                            }}
                        >
                            Excel
                        </Button>
                    </Tooltip> */}
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                        Добавить контакт
                    </Button>
                </Space>
            </div>

            {/* Поиск контактов */}
            <div style={{ marginBottom: 16 }}>
                <Input
                    placeholder="Поиск контактов по имени, username..."
                    prefix={<SearchOutlined />}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    allowClear
                    style={{ maxWidth: 400 }}
                />
            </div>

            <Table
                columns={columns}
                dataSource={contacts.filter(c => {
                    if (!searchQuery) return true
                    const query = searchQuery.toLowerCase()
                    const username = (c.username || '').toLowerCase()
                    const firstName = (c.first_name || '').toLowerCase()
                    const lastName = (c.last_name || '').toLowerCase()
                    const phone = (c.phone || '').toLowerCase()
                    return username.includes(query) || firstName.includes(query) || lastName.includes(query) || phone.includes(query)
                })}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 20 }}
            />

            <Modal
                title={editingContact ? 'Редактировать контакт' : 'Новый контакт'}
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                footer={null}
                width={500}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item
                        name="username"
                        label="Username (без @)"
                        rules={[{ required: !editingContact, message: 'Введите username или телефон' }]}
                    >
                        <Input prefix={<UserOutlined />} placeholder="username" />
                    </Form.Item>

                    <Form.Item name="phone" label="Телефон">
                        <Input placeholder="+79001234567" />
                    </Form.Item>

                    <Form.Item name="first_name" label="Имя">
                        <Input placeholder="Имя" />
                    </Form.Item>

                    <Form.Item name="last_name" label="Фамилия">
                        <Input placeholder="Фамилия" />
                    </Form.Item>

                    <Form.Item name="notes" label="Заметка">
                        <TextArea rows={3} placeholder="Любая информация о контакте..." />
                    </Form.Item>

                    <Form.Item name="tags" label="Теги (через запятую)">
                        <Input placeholder="клиент, важный, VIP" />
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" block>
                            {editingContact ? 'Сохранить' : 'Создать'}
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    )
}

export default Contacts
